'use client';

// app/beta-signup/beta-signup-client.tsx - beta access request form.
// No captcha by design: spam is handled by honeypot + min-fill-time + rate limit.
import { useRef, useState } from 'react';
import Link from 'next/link';
import Turnstile from '@/components/ui/Turnstile';

const inputCls =
  'w-full bg-surface-base border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white/80 placeholder:text-zinc-700 focus:outline-none focus:border-accent/30 transition-colors';

const PRODUCTS = [
  { value: 'AutoScan', label: 'AutoScan', desc: 'External attack surface monitoring & validation' },
  { value: 'AutoFix', label: 'AutoFix', desc: 'Autonomous remediation, CISO-gated' },
  { value: 'Cyber Crawler', label: 'Cyber Crawler', desc: 'Continuous internet exposure discovery' },
];

export default function BetaSignupClient({ defaultProduct, source, siteKey }: { defaultProduct?: string; source?: string; siteKey: string }) {
  // Preselect the product the visitor arrived from (?product= on the URL);
  // fall back to AutoScan. Guard against arbitrary query values.
  const preselected = PRODUCTS.some((p) => p.value === defaultProduct) ? defaultProduct : 'AutoScan';

  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [msg, setMsg] = useState('');
  const [token, setToken] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const startedAt = useRef<number>(Date.now());

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setMsg('');

    const products = form.getAll('products').map(String);
    if (products.length === 0) {
      setMsg('Please select at least one product.');
      return;
    }
    if (siteKey && !token) { setMsg('Please complete the verification check below.'); setState('error'); return; }

    setState('sending');
    try {
      const payload = {
        name: form.get('name'),
        email: form.get('email'),
        company: form.get('company'),
        phone: form.get('phone'),
        country: form.get('country'),
        products,
        notes: form.get('notes'),
        source: source || 'beta_signup_page',
        form_ms: Date.now() - startedAt.current,
        company_url_confirm: form.get('company_url_confirm'),
        turnstileToken: token || undefined,
      };
      const res = await fetch('/api/beta-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await res.json().catch(() => ({} as any));
      if (res.ok && j?.ok !== false) {
        setState('sent');
      } else {
        setState('error');
        setMsg(j?.message || 'Something went wrong. Please try again.');
        setToken(''); setResetKey((k) => k + 1);
      }
    } catch {
      setState('error');
      setMsg('Network error. Please try again.');
    }
  }

  return (
    <section className="relative py-16 min-h-screen bg-surface-base">
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(91, 149, 255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(91, 149, 255,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="relative max-w-2xl mx-auto px-6">
        {/* Header */}
        <div className="mb-12 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent/20 bg-accent/[0.06] text-accent text-xs font-medium mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            Cyber Army · Beta Access
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white/90 tracking-tight mb-4">
            Beta <span className="text-accent">Signup</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-md mx-auto">
            Join early adopters running agentic AI to scan and fix their cybersecurity threats. We respond within 1 business day.
          </p>
        </div>

        {state === 'sent' ? (
          <div className="rounded-2xl border border-accent/20 bg-surface-1 p-12 flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center mb-5">
              <svg className="w-7 h-7 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="text-2xl font-bold text-white/90 mb-3">Request received.</h2>
            <p className="text-sm text-zinc-400 max-w-sm leading-relaxed mb-8">
              Thanks for your interest in Cyber Army. Our team will review your request and reach out within 1 business day.
            </p>
            <Link href="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/10 text-zinc-300 hover:text-white hover:border-white/20 transition-all text-sm">
              Back to home →
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="rounded-2xl border border-white/[0.06] bg-surface-1 p-7 sm:p-9 space-y-5">
            {/* Honeypot */}
            <input type="text" name="company_url_confirm" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-500 mb-1.5 block">Full name *</label>
                <input name="name" required placeholder="Jane Doe" className={inputCls} />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1.5 block">Work email *</label>
                <input name="email" type="email" required placeholder="you@company.com" className={inputCls} />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-500 mb-1.5 block">Company</label>
                <input name="company" placeholder="Acme Corp" className={inputCls} />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1.5 block">Phone <span className="text-zinc-700">(optional)</span></label>
                <input name="phone" type="tel" placeholder="+1 (555) 123-4567" className={inputCls} />
              </div>
            </div>

            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Country <span className="text-zinc-700">(optional)</span></label>
              <input name="country" placeholder="United States" className={inputCls} />
            </div>

            <div>
              <label className="text-xs text-zinc-500 mb-2 block">Products you are interested in *</label>
              <div className="space-y-2.5">
                {PRODUCTS.map((p) => (
                  <label key={p.value} className="flex items-start gap-3 p-3.5 rounded-xl border border-white/[0.08] bg-surface-base cursor-pointer hover:border-accent/25 transition-colors has-[:checked]:border-accent/40 has-[:checked]:bg-accent/[0.04]">
                    <input type="checkbox" name="products" value={p.value} defaultChecked={p.value === preselected} className="mt-0.5 accent-[#5b95ff]" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white/90">{p.label}</div>
                      <div className="text-xs text-zinc-500">{p.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-zinc-500 mb-1.5 block">Anything we should know?</label>
              <textarea name="notes" rows={2} placeholder="Team size, infrastructure, timeline, or what you are hoping to solve..." className={`${inputCls} resize-y`} />
            </div>

            {msg && <p className={`text-sm ${state === 'error' ? 'text-red-400' : 'text-yellow-400'}`}>{msg}</p>}

            <Turnstile siteKey={siteKey} appearance="always" onVerify={setToken} onExpire={() => setToken('')} onError={() => setToken('')} resetKey={resetKey} />

            <button type="submit" disabled={state === 'sending' || (!!siteKey && !token)}
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-accent text-black font-bold hover:bg-accent-hover transition-all duration-300 shadow-lg shadow-accent/20 hover:shadow-accent/35 disabled:opacity-50 disabled:cursor-not-allowed">
              {state === 'sending' ? 'Submitting…' : 'Request Beta Access'}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
            </button>
            <p className="text-[11px] text-zinc-600 text-center">
              We respond within 1 business day. By submitting you agree to our{' '}
              <Link href="/terms" className="underline hover:text-zinc-400 transition-colors">Terms</Link> and{' '}
              <Link href="/privacy" className="underline hover:text-zinc-400 transition-colors">Privacy Policy</Link>.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
